/* eslint-disable import/prefer-default-export */

'use client';

import { ColumnDef, Row } from '@tanstack/react-table';
import { Eye, MoreHorizontal, PenLine, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import parse from 'html-react-parser';
import { Link } from 'react-router';
import { Button } from '../../components/ui/button';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { IReceipt } from '../../types/types';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import useReceipt from '../../stores/useReceipt';

function DropdownReceipt({ row }: { row: Row<IReceipt> }) {
  const receipt = row.original;
  const { deleteReceipt } = useReceipt();
  return (
    <Dialog>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <Link to={`/receipt/view/${receipt.receipt_id}`}>
            <DropdownMenuItem>
              <Eye size={16} />
              View Receipt
            </DropdownMenuItem>
          </Link>
          <DropdownMenuSeparator />
          <Link to={`/receipt/edit/${receipt.receipt_id}`}>
            <DropdownMenuItem>
              <PenLine />
              Edit
            </DropdownMenuItem>
          </Link>
          <DropdownMenuSeparator />
          <DialogTrigger className="w-full">
            <DropdownMenuItem>
              <Trash2 />
              Delete
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>
      <DialogContent className="w-[calc(100dvw-32px)] md:w-full rounded-lg">
        <DialogHeader>
          <DialogTitle>Confirm delete receipt</DialogTitle>
          <DialogDescription className="py-4">
            are you sure you want to delete receipt <span className="font-semibold">{receipt.receipt_number}</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row justify-end gap-2">
          <div>
            <Button variant="default" type="button" onClick={() => deleteReceipt(receipt?.receipt_id as string)}>
              Delete
            </Button>
          </div>
          <DialogClose>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const columns: ColumnDef<IReceipt>[] = [
  {
    accessorKey: 'receipt_number',
    header: 'Receipt No.',
    cell: ({ row }) => <div>{row.getValue('receipt_number')}</div>,
  },
  {
    accessorKey: 'client',
    header: () => <div>Client</div>,
    cell: ({ row }) => {
      const receipt = row.original;
      return <div className="font-medium">{receipt.partner?.name}</div>;
    },
  },
  {
    accessorKey: 'description',
    header: () => <div className=" lg:max-w-80">Description</div>,
    cell: ({ row }) => <div className="font-base line-clamp-1 lg:max-w-80">{parse(row.getValue('description'))}</div>,
  },
  {
    accessorKey: 'received',
    header: () => <div>Finance Account</div>,
    cell: ({ row }) => <div>{row.getValue('received')}</div>,
  },
  {
    accessorKey: 'date',
    header: () => <div>Payment Date</div>,
    cell: ({ row }) => {
      const formattedDate = format(row.getValue('date'), 'LLL dd, y');
      return <div>{formattedDate}</div>;
    },
  },
  {
    accessorKey: 'amount',
    header: () => <div className="text-right">Amount</div>,
    cell: ({ row }) => {
      const amount = parseFloat(row.getValue('amount'));

      const formatted = new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
      }).format(amount);

      return <div className="text-right font-medium">{formatted}</div>;
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => <DropdownReceipt row={row} />,
  },
];
